import { Component, lazy, Suspense, type ErrorInfo, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from '../components/layout/AppShell';
import { DashboardPage } from '../features/dashboard/DashboardPage';
import { ListSkeleton } from '../components/ui/Skeleton';
import { useUiStore } from '../store/ui';

// Universal escape hatch from a render loop: reset to the current exercise and
// drop every persisted view intent (e.g. a stale calendar month of another
// year), then reload. Keeps the user from having to clear localStorage by hand.
function recoverToCurrentExercise() {
  try {
    useUiStore.setState({ selectedYear: new Date().getFullYear(), viewState: {} });
  } catch {
    try {
      window.localStorage.removeItem('aircs-react-ui-v2');
    } catch {
      // localStorage can be unavailable in strict browser contexts.
    }
  }
  window.location.reload();
}

// Lazy-loaded pages (secondary routes)
const PipelinePage = lazy(() => import('../features/pipeline/PipelinePage').then((m) => ({ default: m.PipelinePage })));
const MissionsPage = lazy(() => import('../features/missions/MissionsPage').then((m) => ({ default: m.MissionsPage })));
const ProductionMatrixPage = lazy(() => import('../features/production/ProductionMatrixPage').then((m) => ({ default: m.ProductionMatrixPage })));
const CalendarPage = lazy(() => import('../features/calendar/CalendarPage').then((m) => ({ default: m.CalendarPage })));
const FacturesPage = lazy(() => import('../features/factures/FacturesPage').then((m) => ({ default: m.FacturesPage })));

const ClientsPage = lazy(() => import('../features/clients/ClientsPage').then((m) => ({ default: m.ClientsPage })));
const IntervenantsPage = lazy(() => import('../features/intervenants/IntervenantsPage').then((m) => ({ default: m.IntervenantsPage })));
const Client360Page = lazy(() => import('../features/client360/Client360Page').then((m) => ({ default: m.Client360Page })));
const FinanceOpsPage = lazy(() => import('../features/finance-ops/FinanceOpsPage').then((m) => ({ default: m.FinanceOpsPage })));
const AccountantPackPage = lazy(() => import('../features/accountant-pack/AccountantPackPage').then((m) => ({ default: m.AccountantPackPage })));
const DataQualityPage = lazy(() => import('../features/data-quality/DataQualityPage').then((m) => ({ default: m.DataQualityPage })));
const IntegrationsPage = lazy(() => import('../features/integrations/IntegrationsPage').then((m) => ({ default: m.IntegrationsPage })));
const AiPage = lazy(() => import('../features/ai/AiPage').then((m) => ({ default: m.AiPage })));
const AuditLogPage = lazy(() => import('../features/admin/AuditLogPage').then((m) => ({ default: m.AuditLogPage })));
const PrivacyPage = lazy(() => import('../features/privacy/PrivacyPage').then((m) => ({ default: m.PrivacyPage })));
const MyWeekPage = lazy(() => import('../features/my-week/MyWeekPage').then((m) => ({ default: m.MyWeekPage })));
// Printable Monday plan: a standalone full-screen surface (outside AppShell) so
// the browser print output carries no navigation chrome.
const MondayPlanPage = lazy(() => import('../features/my-week/MondayPlanPage').then((m) => ({ default: m.MondayPlanPage })));

// Auth routes are lazy: they pull in the Supabase SDK, which the normal
// cookie-session flow never touches, so it must stay out of first paint.
const AuthBridgePage = lazy(() => import('../features/auth/AuthBridgePage').then((m) => ({ default: m.AuthBridgePage })));
const EmbedAuthHostPage = lazy(() => import('../features/auth/EmbedAuthHostPage').then((m) => ({ default: m.EmbedAuthHostPage })));
const ExternalAuthPopupPage = lazy(() => import('../features/auth/ExternalAuthPopupPage').then((m) => ({ default: m.ExternalAuthPopupPage })));
const SupabaseAuthLaunchPage = lazy(() => import('../features/auth/SupabaseAuthLaunchPage').then((m) => ({ default: m.SupabaseAuthLaunchPage })));
const SupabasePasswordUpdatePage = lazy(() => import('../features/auth/SupabasePasswordUpdatePage').then((m) => ({ default: m.SupabasePasswordUpdatePage })));

function PageFallback() {
  return <div className="page-stack"><ListSkeleton count={6} /></div>;
}

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[BusinessOS ErrorBoundary]', error.message, info.componentStack);
    const message = `${error.message || ''}\n${error.stack || ''}`.toLowerCase();
    const isStaleChunk =
      message.includes('failed to fetch dynamically imported module') ||
      message.includes('importing a module script failed') ||
      message.includes('loading chunk') ||
      message.includes('chunkloaderror');
    if (isStaleChunk) {
      try {
        const reloadKey = 'businessos-stale-chunk-reload';
        if (!sessionStorage.getItem(reloadKey)) {
          sessionStorage.setItem(reloadKey, '1');
          window.location.reload();
          return;
        }
      } catch {
        window.location.reload();
        return;
      }
    }
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: '2rem', fontFamily: 'monospace' }}>
          <h1>Erreur de rendu</h1>
          <pre style={{ whiteSpace: 'pre-wrap', color: '#c00' }}>{this.state.error.message}</pre>
          <pre style={{ whiteSpace: 'pre-wrap', color: '#666', fontSize: '0.85rem' }}>{this.state.error.stack}</pre>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button onClick={recoverToCurrentExercise}>Revenir à l'exercice courant</button>
            <button onClick={() => { this.setState({ error: null }); window.location.reload(); }}>Recharger</button>
          </div>
        </div>
      );
    }
    try {
      sessionStorage.removeItem('businessos-stale-chunk-reload');
    } catch {
      // sessionStorage can be unavailable in strict browser contexts.
    }
    return this.props.children;
  }
}

export function App() {
  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/auth/external/embed" element={<Suspense fallback={<PageFallback />}><EmbedAuthHostPage /></Suspense>} />
        <Route path="/auth/external/popup" element={<Suspense fallback={<PageFallback />}><ExternalAuthPopupPage /></Suspense>} />
        <Route path="/auth/external/callback" element={<Suspense fallback={<PageFallback />}><AuthBridgePage /></Suspense>} />
        <Route path="/auth/external/supabase" element={<Suspense fallback={<PageFallback />}><SupabaseAuthLaunchPage /></Suspense>} />
        <Route path="/auth/external/supabase/reset" element={<Suspense fallback={<PageFallback />}><SupabasePasswordUpdatePage /></Suspense>} />
        <Route path="/ma-semaine/plan" element={<Suspense fallback={<PageFallback />}><MondayPlanPage /></Suspense>} />
        <Route element={<AppShell />}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/ma-semaine" element={<Suspense fallback={<PageFallback />}><MyWeekPage /></Suspense>} />
          <Route path="/finance" element={<Navigate to="/finance-ops" replace />} />
          <Route path="/finance-ops" element={<Suspense fallback={<PageFallback />}><FinanceOpsPage /></Suspense>} />
          <Route path="/pipeline" element={<Suspense fallback={<PageFallback />}><PipelinePage /></Suspense>} />
          <Route path="/missions" element={<Suspense fallback={<PageFallback />}><MissionsPage /></Suspense>} />
          <Route path="/affectation" element={<Suspense fallback={<PageFallback />}><ProductionMatrixPage /></Suspense>} />
          <Route path="/calendrier" element={<Suspense fallback={<PageFallback />}><CalendarPage /></Suspense>} />
          <Route path="/clients" element={<Suspense fallback={<PageFallback />}><ClientsPage /></Suspense>} />
          <Route path="/intervenants" element={<Suspense fallback={<PageFallback />}><IntervenantsPage /></Suspense>} />
          <Route path="/factures" element={<Suspense fallback={<PageFallback />}><FacturesPage /></Suspense>} />
          <Route path="/actions" element={<Navigate to="/ma-semaine" replace />} />
          <Route path="/pack-comptable" element={<Suspense fallback={<PageFallback />}><AccountantPackPage /></Suspense>} />
          <Route path="/data-quality" element={<Suspense fallback={<PageFallback />}><DataQualityPage /></Suspense>} />

          <Route path="/integrations" element={<Suspense fallback={<PageFallback />}><IntegrationsPage /></Suspense>} />
          <Route path="/client-360" element={<Suspense fallback={<PageFallback />}><Client360Page /></Suspense>} />
          <Route path="/ai" element={<Suspense fallback={<PageFallback />}><AiPage /></Suspense>} />
          <Route path="/admin/audit" element={<Suspense fallback={<PageFallback />}><AuditLogPage /></Suspense>} />
          <Route path="/confidentialite" element={<Suspense fallback={<PageFallback />}><PrivacyPage /></Suspense>} />
          <Route path="/ai-chat" element={<Navigate to="/ai" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Routes>
    </ErrorBoundary>
  );
}
